/**
 * Reminder Service
 * Handles all reminder-related API calls and client-side logic
 */

import api from './api';

class ReminderService {
  /**
   * Initialize default reminders for the current user
   */
  async initializeDefaults() {
    const response = await api.post('/reminders/init');
    return response.data;
  }

  /**
   * Get user reminder preferences
   */
  async getPreferences() {
    const response = await api.get('/reminders/preferences');
    return response.data;
  }

  /**
   * Update user reminder preferences
   */
  async updatePreferences(preferences) {
    const response = await api.put('/reminders/preferences', preferences);
    return response.data;
  }

  /**
   * Get all reminder configurations
   */
  async getConfigs() {
    const response = await api.get('/reminders/configs');
    return response.data.configs;
  }

  /**
   * Create a new reminder configuration
   */
  async createConfig(config) {
    const response = await api.post('/reminders/configs', config);
    return response.data.config;
  }

  /**
   * Update a reminder configuration
   */
  async updateConfig(configId, updates) {
    const response = await api.put(`/reminders/configs/${configId}`, updates);
    return response.data.config;
  }

  /**
   * Delete a reminder configuration
   */
  async deleteConfig(configId) {
    const response = await api.delete(`/reminders/configs/${configId}`);
    return response.data;
  }

  /**
   * Manually trigger a reminder
   */
  async triggerReminder(configId, relatedEntityId = null, relatedEntityType = null) {
    const response = await api.post('/reminders/trigger', {
      config_id: configId,
      related_entity_id: relatedEntityId,
      related_entity_type: relatedEntityType,
    });
    return response.data.instance;
  }

  /**
   * Get active reminder instances
   */
  async getActiveInstances() {
    const response = await api.get('/reminders/instances/active');
    return response.data.instances;
  }

  /**
   * Dismiss a reminder
   */
  async dismissReminder(instanceId) {
    const response = await api.post(`/reminders/instances/${instanceId}/dismiss`);
    return response.data.instance;
  }

  /**
   * Dismiss all active reminders
   */
  async dismissAllReminders() {
    const response = await api.post('/reminders/instances/dismiss-all');
    return response.data;
  }

  /**
   * Snooze a reminder
   */
  async snoozeReminder(instanceId, duration = null) {
    const response = await api.post(`/reminders/instances/${instanceId}/snooze`, {
      duration,
    });
    return response.data.instance;
  }

  /**
   * Get reminder history
   */
  async getHistory(limit = 50) {
    const response = await api.get(`/reminders/history?limit=${limit}`);
    return response.data.history;
  }

  /**
   * Start tracking a task's duration
   */
  async startTaskTracking(taskId, thresholdSeconds = 7200) {
    const response = await api.post('/reminders/task-tracking/start', {
      task_id: taskId,
      threshold_seconds: thresholdSeconds,
    });
    return response.data.tracker;
  }

  /**
   * Stop tracking a task's duration
   */
  async stopTaskTracking(taskId) {
    const response = await api.post('/reminders/task-tracking/stop', {
      task_id: taskId,
    });
    return response.data.tracker;
  }

  /**
   * Check task duration and trigger reminder if needed
   */
  async checkTaskDuration(taskId) {
    const response = await api.post('/reminders/task-tracking/check', {
      task_id: taskId,
    });
    return response.data;
  }

  /**
   * Get all active task trackers
   */
  async getActiveTrackers() {
    const response = await api.get('/reminders/task-tracking/active');
    return response.data.trackers;
  }

  /**
   * Check for overdue tasks and trigger reminder if needed
   */
  async checkOverdueTasks() {
    const response = await api.post('/reminders/check-overdue');
    return response.data;
  }

  /**
   * Get summary of overdue tasks without triggering reminder
   */
  async getOverdueSummary() {
    const response = await api.get('/reminders/overdue-summary');
    return response.data;
  }
}

export default new ReminderService();
